import {
  getCurrentEvent,
  getNextEvent,
} from "core-integration/src/store/events/reducers";
import { getPlayerData } from "core-integration/src/store/player/reducers";
import { useEffect } from "react";
import { WithTranslation, withTranslation } from "react-i18next";
import { useSelector } from "react-redux";
import { Link } from "react-router";
import styled from "styled-components";
// Highlight images
import { RootState } from "core-integration/src/store";
import { getEntry } from "core-integration/src/store/entries/reducers";
import Step1 from "../img/home/step-1@1x.jpg";
import Step1x2 from "../img/home/step-1@2x.jpg";
import Step2 from "../img/home/step-2@1x.jpg";
import Step2x2 from "../img/home/step-2@2x.jpg";
import Button from "./Button";
import { Main, Wrapper } from "./Layout";
import Login from "./Login";
import Status from "./status/Status";
import StatusPreRollover from "./status/StatusPreRollover";
import TriangleCard from "./TriangleCard";
import Video from "./Video";

const VideoNarrow = styled.div`
  @media (min-width: ${({ theme }) => theme.breakpoints[1]}) {
    display: none;
  }
`;

const VideoWide = styled.div`
  display: none;
  @media (min-width: ${({ theme }) => theme.breakpoints[1]}) {
    display: unset;
  }
`;

const Highlights = styled.div`
  display: flex;
  flex-direction: column;
  margin: ${({ theme }) => theme.space[4]} ${({ theme }) => theme.space[2]};

  @media (min-width: ${({ theme }) => theme.breakpoints[2]}) {
    flex-direction: row;
    justify-content: space-between;
  }

  @media (min-width: ${({ theme }) => theme.breakpoints[3]}) {
    margin-inline-start: ${({ theme }) => theme.space[4]};
    margin-inline-end: ${({ theme }) => theme.space[4]};
  }
`;

//<Button> was overriding 'flex: 1' on the flex items - this corrects that
const HighlightsFlexItem = styled.div`
  @media (min-width: ${({ theme }) => theme.breakpoints[2]}) {
    flex: 0 0 calc(50% - 1rem);
  }
`;

const HighlightsItem = styled.div`
  width: 100%;
  height: 100%;
  margin-bottom: ${({ theme }) => theme.space[4]};

  @media (min-width: ${({ theme }) => theme.breakpoints[2]}) {
    flex: 0 0 calc(50% - 1rem);
    margin-bottom: 0;
  }
`;

type Props = WithTranslation;

const Home = ({ t }: Props) => {
  const now = useSelector(getCurrentEvent);
  const nextEvent = useSelector(getNextEvent);
  const player = useSelector(getPlayerData);
  const entry = useSelector((state: RootState) =>
    player?.entry ? getEntry(state, player.entry) : null,
  );

  const TrackFacebook = () => {
    const fbq = (window as any).fbq;
    if (fbq) {
      fbq("trackCustom", "FPLLanding");
    }
  };

  useEffect(() => {
    TrackFacebook();
  }, []);

  // Entry's first event has started: show full Status page on home (live / post-event)
  if (now && player?.entry && entry && entry.started_event <= now.id) {
    return <Status />;
  }

  // Pre 1st rollover only: next event exists but entry not started yet
  if (player && player.entry && nextEvent) {
    return <StatusPreRollover nextEvent={nextEvent} />;
  }

  return (
    <Wrapper>
      <Main isWide={true}>
        <Login />
        <VideoNarrow>
          <Video />
        </VideoNarrow>
        <Highlights>
          {player && !player.entry && (
            <>
              <HighlightsFlexItem>
                <Button to="/squad-selection" variant="home" as={Link}>
                  <HighlightsItem>
                    <TriangleCard
                      title={t("home.highlightsLeftTitle", "Pick your squad")}
                      desc={t(
                        "home.highlightsLeftDesc",
                        "Use your budget of 100M SAR to pick a squad of 15 players from RSL.",
                      )}
                      mdSrc={Step1x2}
                      smSrc={Step1}
                    />
                  </HighlightsItem>
                </Button>
              </HighlightsFlexItem>

              <HighlightsFlexItem>
                <Button to="/squad-selection" variant="home" as={Link}>
                  <HighlightsItem>
                    <TriangleCard
                      title={t(
                        "home.highlightsRightTitle",
                        "Create & join leagues",
                      )}
                      desc={t(
                        "home.highlightsRightDesc",
                        "Play against friends, family members, colleagues or your friends from the web in private leagues.",
                      )}
                      mdSrc={Step2x2}
                      smSrc={Step2}
                    />
                  </HighlightsItem>
                </Button>
              </HighlightsFlexItem>
            </>
          )}

          {player && player.entry && (
            <>
              <HighlightsFlexItem>
                <Button to="/transfers" variant="home" as={Link}>
                  <HighlightsItem>
                    <TriangleCard
                      title={t("home.highlightsLeftTitle", "Pick your squad")}
                      desc={t(
                        "home.highlightsLeftDesc",
                        "Use your budget of 100M SAR to pick a squad of 15 players from RSL.",
                      )}
                      mdSrc={Step1x2}
                      smSrc={Step1}
                    />
                  </HighlightsItem>
                </Button>
              </HighlightsFlexItem>

              <HighlightsFlexItem>
                <Button to="/leagues" variant="home" as={Link}>
                  <HighlightsItem>
                    <TriangleCard
                      title={t(
                        "home.highlightsRightTitle",
                        "Create & join leagues",
                      )}
                      desc={t(
                        "home.highlightsRightDesc",
                        "Play against friends, family members, colleagues or your friends from the web in private leagues.",
                      )}
                      mdSrc={Step2x2}
                      smSrc={Step2}
                    />
                  </HighlightsItem>
                </Button>
              </HighlightsFlexItem>
            </>
          )}

          {!player && (
            <>
              <HighlightsItem>
                <TriangleCard
                  title={t("home.highlightsLeftTitle", "Pick your squad")}
                  desc={t(
                    "home.highlightsLeftDesc",
                    "Use your budget of 100M SAR to pick a squad of 15 players from RSL.",
                  )}
                  mdSrc={Step1x2}
                  smSrc={Step1}
                />
              </HighlightsItem>

              <HighlightsItem>
                <TriangleCard
                  title={t(
                    "home.highlightsRightTitle",
                    "Create & join leagues",
                  )}
                  desc={t(
                    "home.highlightsRightDesc",
                    "Play against friends, family members, colleagues or your friends from the web in private leagues.",
                  )}
                  mdSrc={Step2x2}
                  smSrc={Step2}
                />
              </HighlightsItem>
            </>
          )}
        </Highlights>
        <VideoWide>
          <Video />
        </VideoWide>
      </Main>
    </Wrapper>
  );
};

export default withTranslation()(Home);
